import Papa from "papaparse";
import { hydrateExpenseRecord, hydratePaymentRecord, hydrateTrip, toNumber } from "./rules";
import {
  bulkPutExpenses,
  bulkPutPayments,
  bulkPutTrips,
  loadFullLocalData,
  loadTrips,
} from "./storage";
import {
  BOOKING_TYPES,
  EXPENSE_CATEGORIES,
  PAYMENT_MODES,
  TRIP_STATUSES,
  VEHICLE_SOURCES,
  type BookingType,
  type ExpenseCategory,
  type ExpenseRecord,
  type LegacyTripStatus,
  type PaymentMode,
  type PaymentRecord,
  type Trip,
  type TripStatus,
  type VehicleSource,
} from "./types";

export const TRIP_CSV_COLUMNS: Array<keyof Trip> = [
  "id",
  "tripNo",
  "date",
  "bookingType",
  "vehicleSource",
  "brokerName",
  "vehicleNo",
  "driverName",
  "driverMobile",
  "partyName",
  "origin",
  "destination",
  "hireAmount",
  "advanceReceived",
  "fuelExp",
  "fastagExp",
  "driverBata",
  "commissionExp",
  "otherExp",
  "totalExpense",
  "balance",
  "profit",
  "status",
  "monthKey",
  "notes",
  "searchIndex",
  "settledAt",
  "updatedAt",
];

export const PAYMENT_CSV_COLUMNS: Array<keyof PaymentRecord> = [
  "id",
  "tripId",
  "date",
  "amount",
  "mode",
  "note",
];

export const EXPENSE_CSV_COLUMNS: Array<keyof ExpenseRecord> = [
  "id",
  "tripId",
  "date",
  "category",
  "amount",
  "note",
];

export const BACKUP_CSV_COLUMNS = [
  "recordType",
  ...TRIP_CSV_COLUMNS.map(String),
  "tripId",
  "amount",
  "mode",
  "category",
  "note",
] as const;

export type CsvImportResult = {
  importedCount: number;
  rejectedCount: number;
  errors: string[];
  kind: "workbook" | "backup" | "trips" | "payments" | "expenses";
};

type CsvTripRow = Partial<Record<keyof Trip, unknown>>;
type CsvPaymentRow = Partial<Record<keyof PaymentRecord, unknown>>;
type CsvExpenseRow = Partial<Record<keyof ExpenseRecord, unknown>>;
type CsvBackupRow = Partial<Record<(typeof BACKUP_CSV_COLUMNS)[number], unknown>>;

const isKnownStatus = (
  status: unknown
): status is TripStatus | LegacyTripStatus | undefined => {
  if (!status) return true;
  return status === "Completed" || TRIP_STATUSES.includes(status as TripStatus);
};

const isKnownBookingType = (value: unknown): value is BookingType | undefined => {
  if (!value) return true;
  return BOOKING_TYPES.includes(value as BookingType);
};

const isKnownVehicleSource = (
  value: unknown
): value is VehicleSource | undefined => {
  if (!value) return true;
  return VEHICLE_SOURCES.includes(value as VehicleSource);
};

const isKnownPaymentMode = (value: unknown): value is PaymentMode | undefined => {
  if (!value) return true;
  return PAYMENT_MODES.includes(value as PaymentMode);
};

const isKnownExpenseCategory = (
  value: unknown
): value is ExpenseCategory | undefined => {
  if (!value) return true;
  return EXPENSE_CATEGORIES.includes(value as ExpenseCategory);
};

const asText = (value: unknown): string => String(value ?? "").trim();

export const serializeTripsToCsv = (trips: Trip[]): string =>
  Papa.unparse(
    trips.map((trip) =>
      TRIP_CSV_COLUMNS.reduce<Record<string, string | number>>((row, column) => {
        row[column] = trip[column] ?? "";
        return row;
      }, {})
    ),
    { columns: TRIP_CSV_COLUMNS.map(String) }
  );

export const parseTripsFromRows = (rows: unknown[]): {
  trips: Trip[];
  rejectedCount: number;
  errors: string[];
} => {
  const trips: Trip[] = [];
  const errors: string[] = [];

  rows.forEach((row, index) => {
    const record = row as CsvTripRow;
    const id = asText(record.id);
    const date = asText(record.date);
    const status = asText(record.status);
    const bookingType = asText(record.bookingType);
    const vehicleSource = asText(record.vehicleSource);

    if (!id || !date) {
      errors.push(`Row ${index + 2}: missing trip ID or date.`);
      return;
    }

    if (!isKnownStatus(status)) {
      errors.push(`Row ${index + 2}: unknown status "${status}".`);
      return;
    }

    if (!isKnownBookingType(bookingType)) {
      errors.push(`Row ${index + 2}: unknown booking type "${bookingType}".`);
      return;
    }

    if (!isKnownVehicleSource(vehicleSource)) {
      errors.push(`Row ${index + 2}: unknown vehicle source "${vehicleSource}".`);
      return;
    }

    try {
      const rawTrip = {
        id,
        tripNo: asText(record.tripNo),
        date,
        bookingType: (bookingType || "Direct") as BookingType,
        vehicleSource: (vehicleSource || "Own Vehicle") as VehicleSource,
        brokerName: asText(record.brokerName),
        vehicleNo: asText(record.vehicleNo),
        driverName: asText(record.driverName),
        driverMobile: asText(record.driverMobile),
        partyName: asText(record.partyName),
        origin: asText(record.origin),
        destination: asText(record.destination),
        hireAmount: record.hireAmount,
        advanceReceived: record.advanceReceived,
        fuelExp: record.fuelExp,
        fastagExp: record.fastagExp,
        driverBata: record.driverBata,
        commissionExp: record.commissionExp,
        otherExp: record.otherExp,
        totalExpense: record.totalExpense,
        balance: record.balance,
        profit: record.profit,
        status: (status || "Planned") as TripStatus | LegacyTripStatus,
        monthKey: asText(record.monthKey),
        notes: asText(record.notes),
        searchIndex: asText(record.searchIndex),
        settledAt: asText(record.settledAt) || undefined,
        updatedAt: asText(record.updatedAt) || undefined,
      } as unknown as Trip;

      trips.push(hydrateTrip(rawTrip));
    } catch {
      errors.push(`Row ${index + 2}: could not be converted into a trip.`);
    }
  });

  return {
    trips,
    rejectedCount: errors.length,
    errors,
  };
};

export const serializePaymentsToCsv = (payments: PaymentRecord[]): string =>
  Papa.unparse(
    payments.map((payment) =>
      PAYMENT_CSV_COLUMNS.reduce<Record<string, string | number>>((row, column) => {
        row[column] = payment[column] ?? "";
        return row;
      }, {})
    ),
    { columns: PAYMENT_CSV_COLUMNS.map(String) }
  );

export const serializeExpensesToCsv = (expenses: ExpenseRecord[]): string =>
  Papa.unparse(
    expenses.map((expense) =>
      EXPENSE_CSV_COLUMNS.reduce<Record<string, string | number>>((row, column) => {
        row[column] = expense[column] ?? "";
        return row;
      }, {})
    ),
    { columns: EXPENSE_CSV_COLUMNS.map(String) }
  );

export const serializeLocalBackupToCsv = ({
  trips,
  payments,
  expenses,
}: {
  trips: Trip[];
  payments: PaymentRecord[];
  expenses: ExpenseRecord[];
}): string => {
  const rows = [
    ...trips.map((trip) =>
      BACKUP_CSV_COLUMNS.reduce<Record<string, string | number>>(
        (row, column) => {
          row[column] =
            column === "recordType"
              ? "trip"
              : TRIP_CSV_COLUMNS.includes(column as keyof Trip)
                ? trip[column as keyof Trip] ?? ""
                : "";
          return row;
        },
        {}
      )
    ),
    ...payments.map((payment) =>
      BACKUP_CSV_COLUMNS.reduce<Record<string, string | number>>(
        (row, column) => {
          row[column] =
            column === "recordType"
              ? "payment"
              : PAYMENT_CSV_COLUMNS.includes(column as keyof PaymentRecord)
                ? payment[column as keyof PaymentRecord] ?? ""
                : "";
          return row;
        },
        {}
      )
    ),
    ...expenses.map((expense) =>
      BACKUP_CSV_COLUMNS.reduce<Record<string, string | number>>(
        (row, column) => {
          row[column] =
            column === "recordType"
              ? "expense"
              : EXPENSE_CSV_COLUMNS.includes(column as keyof ExpenseRecord)
                ? expense[column as keyof ExpenseRecord] ?? ""
                : "";
          return row;
        },
        {}
      )
    ),
  ];

  return Papa.unparse(rows, { columns: [...BACKUP_CSV_COLUMNS] });
};

export const parseBackupFromRows = (
  rows: Array<Record<string, unknown>>
): {
  trips: Trip[];
  payments: PaymentRecord[];
  expenses: ExpenseRecord[];
  rejectedCount: number;
  errors: string[];
} => {
  const tripRows: Array<Record<string, unknown>> = [];
  const paymentRows: Array<Record<string, unknown>> = [];
  const expenseRows: Array<Record<string, unknown>> = [];
  const errors: string[] = [];

  rows.forEach((row, index) => {
    const record = row as CsvBackupRow;
    const recordType = asText(record.recordType).toLowerCase();

    if (recordType === "trip" || recordType === "trips") {
      tripRows.push(row);
      return;
    }

    if (recordType === "payment" || recordType === "payments") {
      paymentRows.push(row);
      return;
    }

    if (recordType === "expense" || recordType === "expenses") {
      expenseRows.push(row);
      return;
    }

    errors.push(`Row ${index + 2}: unknown backup record type "${recordType}".`);
  });

  const trips = parseTripsFromRows(tripRows);
  const payments = parsePaymentsFromRows(paymentRows);
  const expenses = parseExpensesFromRows(expenseRows);

  return {
    trips: trips.trips,
    payments: payments.payments,
    expenses: expenses.expenses,
    rejectedCount:
      errors.length +
      trips.rejectedCount +
      payments.rejectedCount +
      expenses.rejectedCount,
    errors: [...errors, ...trips.errors, ...payments.errors, ...expenses.errors],
  };
};

export const parsePaymentsFromRows = (rows: unknown[]): {
  payments: PaymentRecord[];
  rejectedCount: number;
  errors: string[];
} => {
  const payments: PaymentRecord[] = [];
  const errors: string[] = [];

  rows.forEach((row, index) => {
    const record = row as CsvPaymentRow;
    const id = asText(record.id);
    const tripId = asText(record.tripId);
    const date = asText(record.date);
    const mode = asText(record.mode);

    if (!id || !tripId || !date || toNumber(record.amount) <= 0) {
      errors.push(`Row ${index + 2}: missing payment ID, trip ID, date, or amount.`);
      return;
    }

    if (!isKnownPaymentMode(mode)) {
      errors.push(`Row ${index + 2}: unknown payment mode "${mode}".`);
      return;
    }

    payments.push(
      hydratePaymentRecord({
        id,
        tripId,
        date,
        amount: toNumber(record.amount),
        mode: (mode || "Cash") as PaymentMode,
        note: asText(record.note),
      })
    );
  });

  return { payments, rejectedCount: errors.length, errors };
};

export const parseExpensesFromRows = (rows: unknown[]): {
  expenses: ExpenseRecord[];
  rejectedCount: number;
  errors: string[];
} => {
  const expenses: ExpenseRecord[] = [];
  const errors: string[] = [];

  rows.forEach((row, index) => {
    const record = row as CsvExpenseRow;
    const id = asText(record.id);
    const tripId = asText(record.tripId);
    const date = asText(record.date);
    const category = asText(record.category);

    if (!id || !tripId || !date || toNumber(record.amount) <= 0) {
      errors.push(`Row ${index + 2}: missing expense ID, trip ID, date, or amount.`);
      return;
    }

    if (!isKnownExpenseCategory(category)) {
      errors.push(`Row ${index + 2}: unknown expense category "${category}".`);
      return;
    }

    expenses.push(
      hydrateExpenseRecord({
        id,
        tripId,
        date,
        category: (category || "Other") as ExpenseCategory,
        amount: toNumber(record.amount),
        note: asText(record.note),
      })
    );
  });

  return { expenses, rejectedCount: errors.length, errors };
};

const parseCsvRowsFile = (file: File): Promise<{
  rows: Array<Record<string, unknown>>;
  errors: string[];
}> =>
  new Promise((resolve, reject) => {
    Papa.parse<Record<string, unknown>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        const parseErrors = result.errors.map(
          (error) => `Row ${error.row ?? "unknown"}: ${error.message}`
        );
        resolve({ rows: result.data, errors: parseErrors });
      },
      error: (error) => reject(error),
    });
  });

export const parseTripsCsvFile = async (file: File): Promise<{
  trips: Trip[];
  rejectedCount: number;
  errors: string[];
}> => {
  const result = await parseCsvRowsFile(file);
  const parsed = parseTripsFromRows(result.rows);
  return {
    trips: parsed.trips,
    rejectedCount: parsed.rejectedCount + result.errors.length,
    errors: [...parsed.errors, ...result.errors],
  };
};

export const triggerCsvDownload = (csv: string, filename: string): void => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

export const exportTripsToCsvFile = async (): Promise<number> => {
  const { trips, payments, expenses } = await loadFullLocalData();
  const today = new Date().toISOString().slice(0, 10);

  const csv = serializeLocalBackupToCsv({ trips, payments, expenses });
  triggerCsvDownload(csv, `haulledger-backup-${today}.csv`);

  return trips.length + payments.length + expenses.length;
};

export const importTripsFromCsvFile = async (
  file: File
): Promise<CsvImportResult> => {
  const result = await parseCsvRowsFile(file);
  const firstRow = result.rows[0] ?? {};

  if ("recordType" in firstRow) {
    const parsed = parseBackupFromRows(result.rows);
    const [tripCount, paymentCount, expenseCount] = await Promise.all([
      bulkPutTrips(parsed.trips),
      bulkPutPayments(parsed.payments),
      bulkPutExpenses(parsed.expenses),
    ]);

    return {
      importedCount: tripCount + paymentCount + expenseCount,
      rejectedCount: parsed.rejectedCount + result.errors.length,
      errors: [...parsed.errors, ...result.errors],
      kind: "backup",
    };
  }

  if ("category" in firstRow && "tripId" in firstRow) {
    const parsed = parseExpensesFromRows(result.rows);
    const importedCount = await bulkPutExpenses(parsed.expenses);
    return {
      importedCount,
      rejectedCount: parsed.rejectedCount + result.errors.length,
      errors: [...parsed.errors, ...result.errors],
      kind: "expenses",
    };
  }

  if ("mode" in firstRow && "tripId" in firstRow) {
    const parsed = parsePaymentsFromRows(result.rows);
    const importedCount = await bulkPutPayments(parsed.payments);
    return {
      importedCount,
      rejectedCount: parsed.rejectedCount + result.errors.length,
      errors: [...parsed.errors, ...result.errors],
      kind: "payments",
    };
  }

  const parsed = parseTripsFromRows(result.rows);
  const importedCount = await bulkPutTrips(parsed.trips);

  return {
    importedCount,
    rejectedCount: parsed.rejectedCount + result.errors.length,
    errors: [...parsed.errors, ...result.errors],
    kind: "trips",
  };
};

export const exportPartyStatementToCsvFile = async (
  partyName: string
): Promise<number> => {
  const trips = (await loadTrips()).filter((trip) => trip.partyName === partyName);
  const csv = Papa.unparse(
    trips.map((trip) => {
      const financials = {
        received: trip.advanceReceived,
        outstanding: trip.balance,
      };
      return {
        date: trip.date,
        tripNo: trip.tripNo,
        vehicleNo: trip.vehicleNo,
        route: [trip.origin, trip.destination].filter(Boolean).join(" - "),
        hireAmount: trip.hireAmount,
        received: financials.received,
        outstanding: financials.outstanding,
        status: trip.status,
      };
    }),
    {
      columns: [
        "date",
        "tripNo",
        "vehicleNo",
        "route",
        "hireAmount",
        "received",
        "outstanding",
        "status",
      ],
    }
  );
  const today = new Date().toISOString().slice(0, 10);
  const safeParty = partyName.toLowerCase().replace(/[^a-z0-9]+/g, "-") || "party";
  triggerCsvDownload(csv, `party-statement-${safeParty}-${today}.csv`);
  return trips.length;
};
